import { useMemo, useState } from 'react';
import { featuredComics, type ComicItem } from '@/mocks/comics';

interface HotComicsProps {
  activeCate: string;
  onCateChange: (cate: string) => void;
}

export default function HotComics({ activeCate, onCateChange }: HotComicsProps) {
  const [keyword, setKeyword] = useState('');
  const cateList = ['全部', ...Array.from(new Set(featuredComics.map((c) => c.category)))];

  const list = useMemo(() => {
    const kw = keyword.trim();
    return featuredComics.filter((c) => {
      const cateOk = activeCate === '全部' || c.category === activeCate;
      const kwOk =
        !kw ||
        c.title.includes(kw) ||
        c.author.includes(kw) ||
        c.tags.some((t) => t.includes(kw)) ||
        c.category.includes(kw);
      return cateOk && kwOk;
    });
  }, [activeCate, keyword]);

  const fmt = (n: number) => (n >= 10000 ? `${(n / 10000).toFixed(1)}万` : `${n}`);

  return (
    <section id="hot" className="relative py-16 md:py-24">
      <div className="container-site">
        <div className="reveal flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-primary-400">
              一起看漫画专区
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
              和漫友一起同看的火热作品
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              每一部都支持同看、一起追，实时同步更新。挑一部心仪的，点开就能拉上漫友开看。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 作者 / 标签"
                className="h-11 w-full rounded-full border border-background-300/60 bg-background-100/60 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-400 sm:w-64"
              />
            </div>
          </div>
        </div>

        <div className="reveal mt-6 flex flex-wrap gap-2">
          {cateList.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onCateChange(c)}
              className={`chip border ${
                activeCate === c
                  ? 'border-primary-400 bg-primary-500 text-background-50'
                  : 'border-background-300/60 bg-background-100/50 text-foreground-600 hover:border-primary-400/60 hover:text-primary-300'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          共 {list.length} 部作品，实时同步更新中
        </p>

        {list.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-background-300/60 bg-background-100/40 py-16 text-center">
            <i className="ri-search-line text-4xl text-foreground-500"></i>
            <p className="mt-3 text-sm text-foreground-500">没有找到匹配的作品，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((c, idx) => (
              <ComicCard key={c.id} comic={c} idx={idx} fmt={fmt} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ComicCard({ comic, idx, fmt }: { comic: ComicItem; idx: number; fmt: (n: number) => string }) {
  return (
    <article
      className={`reveal group overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/60 transition-all hover:-translate-y-1.5 hover:border-accent-400/50`}
      style={{ transitionDelay: `${(idx % 4) * 70}ms` }}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} 漫画封面，适合和漫友一起同看追更`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            comic.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-accent-500 text-background-50'
          }`}
        >
          {comic.status}
        </span>
        <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-background-50/80 px-2.5 py-1 text-[11px] font-semibold text-secondary-600 backdrop-blur">
          <i className="ri-star-fill text-xs"></i>
          {comic.rating}
        </span>
      </div>

      <div className="p-4">
        <h3 className="truncate font-heading text-lg text-foreground-950">{comic.title}</h3>
        <p className="mt-1 text-xs text-foreground-500">
          {comic.author} · {comic.chapters} 话
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {comic.tags.map((t) => (
            <span
              key={t}
              className="rounded-md bg-secondary-500/15 px-2 py-0.5 text-[11px] text-secondary-400"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-team-line text-primary-400"></i>
            {fmt(comic.followers)} 人同看
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-play-circle-fill"></i>
            一起看
          </button>
        </div>
      </div>
    </article>
  );
}